import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, X, ShoppingBag } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { SectionLink } from "@/components/SectionLink";
import { ShareButton } from "@/components/ShareButton";


const btn = "absolute w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white grid place-items-center transition-colors";

export const Lightbox = ({ photos, index, onChange, onClose }) => {
  const startX = useRef(null);
  const go = (d) => onChange((index + d + photos.length) % photos.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  });

  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  return (
    <div
      data-testid={HOME.lightbox}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] bg-[#12100E]/95 backdrop-blur-sm grid place-items-center fade-in"
      onClick={onClose}
      onTouchStart={(e) => (startX.current = e.touches[0].clientX)}
      onTouchEnd={onTouchEnd}
    >
      <figure onClick={(e) => e.stopPropagation()} className="flex flex-col items-center">
        <img
          key={index}
          src={photos[index].src}
          alt={photos[index].name}
          data-testid={HOME.lightboxImage}
          className="max-h-[62vh] max-w-[92vw] rounded-3xl object-contain fade-in"
        />
        <figcaption key={`c${index}`} className="mt-5 text-center fade-in">
          <p data-testid={HOME.lightboxName} className="font-heading text-xl sm:text-2xl font-bold text-white">{photos[index].name}</p>
          {photos[index].price && (
            <p data-testid={HOME.lightboxPrice} className="mt-1 text-lg font-semibold text-[#F97316]">{photos[index].price}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <SectionLink
              url={photos[index].buyUrl}
              onClick={onClose}
              data-testid={HOME.lightboxBuy}
              className="inline-flex items-center gap-2 bg-[#E06D44] text-white px-6 py-3 rounded-full font-medium hover:bg-[#C95B33] transition-colors"
            >
              <ShoppingBag size={16} /> Buy in the Shop
            </SectionLink>
            <ShareButton key={index} name={photos[index].name} url={`${window.location.origin}/?product=${photos[index].slug}`} />
          </div>
        </figcaption>
      </figure>
      <button data-testid={HOME.lightboxClose} aria-label="Close" onClick={onClose} className={`${btn} top-5 right-5`}><X size={22} /></button>
      <button data-testid={HOME.lightboxPrev} aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }} className={`${btn} left-4 top-1/2 -translate-y-1/2`}><ChevronLeft size={24} /></button>
      <button data-testid={HOME.lightboxNext} aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }} className={`${btn} right-4 top-1/2 -translate-y-1/2`}><ChevronRight size={24} /></button>
      <p data-testid={HOME.lightboxCounter} className="absolute top-8 left-6 text-sm text-stone-300 tracking-widest">{index + 1} / {photos.length}</p>
    </div>
  );
};
